import React from "react";
import { Box, Sparkles, Code, MousePointer, Star } from "lucide-react";
import { ComponentCategory, ComponentItem } from "./types";

export const componentCategories: ComponentCategory[] = [
  {
    id: "all",
    name: "All Components",
    icon: <Box size={14} />
  }, 
  {
    id: "layout",
    name: "Layout",
    icon: <MousePointer size={14} />
  }, 
  {
    id: "forms",
    name: "Forms",
    icon: <Code size={14} />
  }, 
  {
    id: "navigation",
    name: "Navigation",
    icon: <Star size={14} />
  }, 
  {
    id: "display",
    name: "Display",
    icon: <Sparkles size={14} />
  }
];

export const components: ComponentItem[] = [
  {
    id: "product-card",
    name: "Product Card",
    category: "display",
    complexity: "medium",
    tags: ["card", "ecommerce", "product", "retail"],
    version: "1.2.0",
    createdAt: "2023-07-15",
    updatedAt: "2023-10-28",
    code: `<Card className="overflow-hidden bg-background border-border hover:shadow-lg transition-shadow duration-300 flex flex-col h-full">
  <div className="aspect-square bg-gradient-to-br from-primary/5 to-background flex items-center justify-center relative">
    <div className="absolute top-2 right-2 bg-accent/90 text-accent-foreground px-2 py-0.5 text-xs font-medium rounded-full">
      NEW
    </div>
    <div className="w-2/3 h-2/3 bg-muted/50 rounded-md flex items-center justify-center text-muted-foreground font-medium">
      Product Image
    </div>
  </div>
  <CardContent className="p-4 flex-1 flex flex-col">
    <div className="flex items-start justify-between mb-2">
      <h3 className="font-medium text-lg">Product Name</h3>
      <span className="text-primary font-bold">$99.99</span>
    </div>
    <p className="text-sm text-muted-foreground mb-4">High-quality product with amazing features that you'll absolutely love.</p>
    <div className="mt-auto">
      <Button className="w-full">Add to Cart</Button>
    </div>
  </CardContent>
</Card>`,
    preview: `
        <div class="overflow-hidden bg-background border border-border rounded-md flex flex-col h-full">
          <div class="relative aspect-square bg-gradient-to-br from-primary/5 to-background flex items-center justify-center">
            <div class="absolute top-2 right-2 bg-primary/90 text-primary-foreground px-2 py-0.5 text-xs font-medium rounded-full">
              NEW
            </div>
            <div class="w-2/3 h-2/3 bg-muted/50 rounded-md flex items-center justify-center text-muted-foreground font-medium">
              Product Image
            </div>
          </div>
          <div class="p-4 flex-1 flex flex-col">
            <div class="flex items-start justify-between mb-2">
              <h3 class="font-medium text-lg">Product Name</h3>
              <span class="text-primary font-bold">$99.99</span>
            </div>
            <p class="text-sm text-muted-foreground mb-4">High-quality product with amazing features.</p>
            <div class="mt-auto">
              <button class="w-full bg-primary text-primary-foreground px-4 py-2 rounded-md">Add to Cart</button>
            </div>
          </div>
        </div>
      `
  },
  {
    id: "contact-form",
    name: "Contact Form",
    category: "forms",
    complexity: "simple",
    tags: ["form", "contact", "input", "validation"],
    version: "1.0.1",
    createdAt: "2023-08-05",
    updatedAt: "2023-09-12",
    code: `<div className="max-w-md mx-auto p-6 bg-card rounded-lg border border-border shadow-sm">
  <h2 className="text-2xl font-bold mb-6 text-center">Contact Us</h2>
  <form className="space-y-4">
    <div className="space-y-2">
      <label htmlFor="name" className="block text-sm font-medium">
        Name
      </label>
      <input
        id="name"
        type="text"
        placeholder="Your name"
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
      />
    </div>
    
    <div className="space-y-2">
      <label htmlFor="email" className="block text-sm font-medium">
        Email
      </label>
      <input
        id="email"
        type="email"
        placeholder="Your email"
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
      />
    </div>
    
    <div className="space-y-2">
      <label htmlFor="message" className="block text-sm font-medium">
        Message
      </label>
      <textarea
        id="message"
        placeholder="Your message"
        rows={4}
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[100px]"
      />
    </div>
    
    <div className="pt-2">
      <button 
        type="submit" 
        className="w-full bg-primary text-primary-foreground py-2 rounded-md hover:bg-primary/90 transition-colors"
      >
        Send Message
      </button>
    </div>
  </form>
</div>`,
    preview: `
        <div class="max-w-full mx-auto p-4 bg-card rounded-lg border border-border shadow-sm">
          <h2 class="text-lg font-bold mb-4 text-center">Contact Us</h2>
          <form class="space-y-3">
            <div class="space-y-1">
              <label class="block text-xs font-medium">Name</label>
              <input
                type="text"
                placeholder="Your name"
                class="w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs"
              />
            </div>
            <div class="space-y-1">
              <label class="block text-xs font-medium">Email</label>
              <input
                type="email"
                placeholder="Your email"
                class="w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs"
              />
            </div>
            <div class="space-y-1">
              <label class="block text-xs font-medium">Message</label>
              <textarea
                placeholder="Your message"
                class="w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs min-h-[60px]"
              ></textarea>
            </div>
            <button 
              type="submit" 
              class="w-full bg-primary text-primary-foreground py-1.5 rounded-md hover:bg-primary/90 transition-colors text-xs font-medium"
            >
              Send Message
            </button>
          </form>
        </div>
      `
  },
  {
    id: "navigation-bar",
    name: "Navigation Bar",
    category: "navigation",
    complexity: "complex",
    tags: ["navbar", "header", "responsive", "menu"],
    version: "2.1.0",
    createdAt: "2023-06-18",
    updatedAt: "2023-11-10",
    code: `<header className="bg-background border-b border-border">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="flex items-center justify-between h-16">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <span className="font-bold text-xl bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            Brand
          </span>
        </div>
        <nav className="hidden md:block ml-10">
          <div className="flex items-center space-x-6">
            <a href="#" className="text-foreground hover:text-primary border-b-2 border-primary px-1 py-2 font-medium text-sm">
              Home
            </a>
            <a href="#" className="text-muted-foreground hover:text-foreground px-1 py-2 font-medium text-sm">
              Features
            </a>
            <a href="#" className="text-muted-foreground hover:text-foreground px-1 py-2 font-medium text-sm">
              Pricing
            </a>
            <a href="#" className="text-muted-foreground hover:text-foreground px-1 py-2 font-medium text-sm">
              About
            </a>
            <a href="#" className="text-muted-foreground hover:text-foreground px-1 py-2 font-medium text-sm">
              Contact
            </a>
          </div>
        </nav>
      </div>
      <div className="hidden md:block">
        <div className="flex items-center space-x-4">
          <a href="#" className="text-muted-foreground hover:text-foreground px-3 py-2 font-medium text-sm">
            Sign In
          </a>
          <a href="#" className="bg-primary text-primary-foreground hover:bg-primary/90 px-4 py-2 rounded-md font-medium text-sm">
            Get Started
          </a>
        </div>
      </div>
      <div className="md:hidden">
        <button className="text-muted-foreground hover:text-foreground p-2">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="h-6 w-6">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</header>`,
    preview: `
        <div class="bg-background border border-border p-3 rounded-md">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <span class="font-bold text-base bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                  Brand
                </span>
              </div>
              <nav class="ml-6">
                <div class="flex items-center space-x-3 text-xs">
                  <a href="#" class="text-foreground border-b-2 border-primary px-1 font-medium">Home</a>
                  <a href="#" class="text-muted-foreground hover:text-foreground px-1 font-medium">Features</a>
                  <a href="#" class="text-muted-foreground hover:text-foreground px-1 font-medium">Pricing</a>
                </div>
              </nav>
            </div>
            <div class="flex items-center space-x-2">
              <button class="px-2 py-1 border border-border rounded-md text-xs font-medium bg-secondary text-foreground">
                Login
              </button>
              <button class="px-2 py-1 rounded-md text-xs font-medium bg-primary text-primary-foreground">
                Sign up
              </button>
            </div>
          </div>
        </div>
      `
  },
  {
    id: "grid-layout",
    name: "Grid Layout",
    category: "layout",
    complexity: "medium",
    tags: ["grid", "layout", "responsive", "cards"],
    version: "1.0.0",
    createdAt: "2023-09-02",
    updatedAt: "2023-11-05",
    code: `<div className="container mx-auto px-4 py-8">
  <h2 className="text-2xl font-bold mb-6">Featured Products</h2>
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
        <div className="aspect-video bg-muted flex items-center justify-center">
          <span className="text-muted-foreground">Image {i + 1}</span>
        </div>
        <div className="p-4">
          <h3 className="font-medium mb-1">Product Title</h3>
          <p className="text-sm text-muted-foreground mb-3">Brief description of the product goes here.</p>
          <div className="flex items-center justify-between">
            <span className="font-bold text-primary">$49.99</span>
            <button className="px-3 py-1 bg-primary/10 text-primary rounded-md text-sm hover:bg-primary/20 transition-colors">
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    ))}
  </div>
</div>`,
    preview: `
        <div class="container mx-auto">
          <h2 class="text-base font-bold mb-3">Featured Products</h2>
          <div class="grid grid-cols-2 gap-2">
            <div class="bg-card border border-border rounded-lg overflow-hidden">
              <div class="aspect-video bg-muted flex items-center justify-center">
                <span class="text-muted-foreground text-xs">Image 1</span>
              </div>
              <div class="p-2">
                <h3 class="font-medium text-xs">Product Title</h3>
                <p class="text-xs text-muted-foreground my-1">Brief description here.</p>
                <div class="flex items-center justify-between">
                  <span class="font-bold text-primary text-xs">$49.99</span>
                  <button class="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs">
                    Add
                  </button>
                </div>
              </div>
            </div>
            <div class="bg-card border border-border rounded-lg overflow-hidden">
              <div class="aspect-video bg-muted flex items-center justify-center">
                <span class="text-muted-foreground text-xs">Image 2</span>
              </div>
              <div class="p-2">
                <h3 class="font-medium text-xs">Product Title</h3>
                <p class="text-xs text-muted-foreground my-1">Brief description here.</p>
                <div class="flex items-center justify-between">
                  <span class="font-bold text-primary text-xs">$49.99</span>
                  <button class="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs">
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      `
  },
  {
    id: "testimonial-card",
    name: "Testimonial Card",
    category: "display",
    complexity: "simple",
    tags: ["card", "testimonial", "review", "social proof"],
    version: "1.1.0",
    createdAt: "2023-08-18",
    updatedAt: "2023-10-30",
    code: `<div className="bg-card border border-border rounded-lg p-6 shadow-sm">
  <div className="flex items-center space-x-4 mb-4">
    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
      <span className="text-primary font-bold">JD</span>
    </div>
    <div>
      <h3 className="font-medium">Jane Doe</h3>
      <p className="text-sm text-muted-foreground">CEO at TechCorp</p>
    </div>
  </div>
  <div className="mb-4">
    <div className="flex text-yellow-500 mb-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <svg key={i} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path fillRule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clipRule="evenodd" />
        </svg>
      ))}
    </div>
    <blockquote className="text-foreground italic">
      "We've been using this product for over a year now and it has completely transformed our workflow. The team behind it is responsive and continuously adding new features."
    </blockquote>
  </div>
  <div className="pt-4 border-t border-border">
    <p className="text-sm text-muted-foreground">Posted on January 12, 2023</p>
  </div>
</div>`,
    preview: `
        <div class="bg-card border border-border rounded-lg p-3 shadow-sm">
          <div class="flex items-center space-x-2 mb-2">
            <div class="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
              <span class="text-primary font-bold text-xs">JD</span>
            </div>
            <div>
              <h3 class="font-medium text-xs">Jane Doe</h3>
              <p class="text-xs text-muted-foreground">CEO at TechCorp</p>
            </div>
          </div>
          <div class="mb-2">
            <div class="flex text-yellow-500 mb-1">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-3 h-3">
                <path fill-rule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clip-rule="evenodd" />
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-3 h-3">
                <path fill-rule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clip-rule="evenodd" />
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-3 h-3">
                <path fill-rule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clip-rule="evenodd" />
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-3 h-3">
                <path fill-rule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clip-rule="evenodd" />
              </svg>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-3 h-3">
                <path fill-rule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z" clip-rule="evenodd" />
              </svg>
            </div>
            <blockquote class="text-foreground italic text-xs leading-tight">
              "We've been using this product for over a year now and it has completely transformed our workflow."
            </blockquote>
          </div>
          <div class="pt-2 border-t border-border">
            <p class="text-xs text-muted-foreground">Posted on January 12, 2023</p>
          </div>
        </div>
      `
  },
  {
    id: "flexbox-layout",
    name: "Flexbox Layout",
    category: "layout",
    complexity: "simple",
    tags: ["flexbox", "layout", "responsive"],
    version: "1.0.2",
    createdAt: "2023-07-22",
    updatedAt: "2023-09-15",
    code: `<div className="max-w-7xl mx-auto p-6">
  <div className="flex flex-wrap md:flex-nowrap gap-6">
    <div className="w-full md:w-1/3 bg-card rounded-lg p-6 border border-border">
      <h3 className="text-lg font-semibold mb-4">Sidebar Content</h3>
      <p className="text-muted-foreground">This is a flexible sidebar that adapts to different screen sizes.</p>
    </div>
    <div className="w-full md:w-2/3 bg-card rounded-lg p-6 border border-border">
      <h2 className="text-2xl font-bold mb-4">Main Content</h2>
      <p className="text-muted-foreground mb-4">This is the main content area that takes up more space.</p>
      <div className="flex flex-wrap gap-4">
        <div className="w-full sm:w-1/2 bg-muted p-4 rounded-md">Flex Item 1</div>
        <div className="w-full sm:w-1/2 bg-muted p-4 rounded-md">Flex Item 2</div>
      </div>
    </div>
  </div>
</div>`,
    preview: `
    <div class="p-2">
      <div class="flex flex-wrap gap-2">
        <div class="w-1/3 bg-card rounded p-2 border border-border">
          <h3 class="text-xs font-semibold mb-1">Sidebar</h3>
          <p class="text-xs text-muted-foreground">Flexible sidebar content.</p>
        </div>
        <div class="w-2/3 bg-card rounded p-2 border border-border">
          <h2 class="text-xs font-semibold mb-1">Main</h2>
          <div class="flex gap-1">
            <div class="w-1/2 bg-muted p-1 rounded text-xs">Item 1</div>
            <div class="w-1/2 bg-muted p-1 rounded text-xs">Item 2</div>
          </div>
        </div>
      </div>
    </div>
  `
  },
  {
    id: "sidebar-layout",
    name: "Sidebar Layout",
    category: "layout",
    complexity: "medium",
    tags: ["sidebar", "layout", "navigation", "dashboard"],
    version: "1.2.1",
    createdAt: "2023-06-30",
    updatedAt: "2023-10-10",
    code: `<div className="min-h-screen flex">
  <aside className="w-64 bg-card border-r border-border">
    <div className="p-6">
      <h3 className="text-lg font-semibold mb-4">Dashboard</h3>
      <nav className="space-y-2">
        <a href="#" className="block px-4 py-2 rounded-md bg-primary text-primary-foreground">Home</a>
        <a href="#" className="block px-4 py-2 rounded-md text-foreground hover:bg-muted">Analytics</a>
        <a href="#" className="block px-4 py-2 rounded-md text-foreground hover:bg-muted">Settings</a>
      </nav>
    </div>
  </aside>
  <main className="flex-1 p-6 bg-background">
    <h2 className="text-2xl font-bold mb-4">Main Content</h2>
    <p className="text-muted-foreground">Your content goes here.</p>
  </main>
</div>`,
    preview: `
    <div class="flex h-32 border border-border rounded overflow-hidden">
      <aside class="w-16 bg-card border-r border-border p-2">
        <h3 class="text-xs font-semibold mb-1">Menu</h3>
        <div class="space-y-1">
          <div class="bg-primary text-primary-foreground text-xs p-1 rounded">Home</div>
          <div class="text-xs p-1">More</div>
        </div>
      </aside>
      <main class="flex-1 p-2 bg-background">
        <h2 class="text-xs font-semibold">Content</h2>
      </main>
    </div>
  `
  },
  {
    id: "login-form",
    name: "Login Form",
    category: "forms",
    complexity: "simple",
    tags: ["form", "login", "authentication", "input"],
    version: "1.1.0",
    createdAt: "2023-08-12",
    updatedAt: "2023-11-01",
    code: `<div className="max-w-sm mx-auto p-6 bg-card rounded-lg border border-border">
  <h2 className="text-2xl font-bold text-center mb-6">Login</h2>
  <form className="space-y-4">
    <div className="space-y-2">
      <label htmlFor="email" className="block text-sm font-medium">
        Email
      </label>
      <input
        id="email"
        type="email"
        placeholder="Enter your email"
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        required
      />
    </div>
    <div className="space-y-2">
      <label htmlFor="password" className="block text-sm font-medium">
        Password
      </label>
      <input
        id="password"
        type="password"
        placeholder="Enter your password"
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        required
      />
    </div>
    <div className="flex items-center justify-between">
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" className="rounded border-input" />
        Remember me
      </label>
      <a href="#" className="text-sm text-primary hover:underline">
        Forgot password?
      </a>
    </div>
    <button 
      type="submit" 
      className="w-full bg-primary text-primary-foreground py-2 rounded-md hover:bg-primary/90 transition-colors"
    >
      Sign in
    </button>
  </form>
</div>`,
    preview: `
    <div class="max-w-full mx-auto p-3 bg-card rounded-lg border border-border">
      <h2 class="text-sm font-bold text-center mb-2">Login</h2>
      <form class="space-y-2">
        <div>
          <label class="block text-xs font-medium">Email</label>
          <input type="email" class="w-full rounded border border-input bg-background px-2 py-1 text-xs" />
        </div>
        <div>
          <label class="block text-xs font-medium">Password</label>
          <input type="password" class="w-full rounded border border-input bg-background px-2 py-1 text-xs" />
        </div>
        <button class="w-full bg-primary text-primary-foreground py-1 rounded text-xs">Sign in</button>
      </form>
    </div>
  `
  },
  {
    id: "registration-form",
    name: "Registration Form",
    category: "forms",
    complexity: "complex",
    tags: ["form", "registration", "signup", "authentication"],
    version: "1.0.1",
    createdAt: "2023-08-05",
    updatedAt: "2023-10-25",
    code: `<div className="max-w-md mx-auto p-6 bg-card rounded-lg border border-border">
  <h2 className="text-2xl font-bold text-center mb-6">Create Account</h2>
  <form className="space-y-4">
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <label htmlFor="first-name" className="block text-sm font-medium">
          First Name
        </label>
        <input
          id="first-name"
          type="text"
          placeholder="John"
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          required
        />
      </div>
      <div className="space-y-2">
        <label htmlFor="last-name" className="block text-sm font-medium">
          Last Name
        </label>
        <input
          id="last-name"
          type="text"
          placeholder="Doe"
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          required
        />
      </div>
    </div>
    <div className="space-y-2">
      <label htmlFor="email" className="block text-sm font-medium">
        Email
      </label>
      <input
        id="email"
        type="email"
        placeholder="john.doe@example.com"
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        required
      />
    </div>
    <div className="space-y-2">
      <label htmlFor="password" className="block text-sm font-medium">
        Password
      </label>
      <input
        id="password"
        type="password"
        placeholder="Create a password"
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        required
      />
    </div>
    <div className="space-y-2">
      <label htmlFor="confirm-password" className="block text-sm font-medium">
        Confirm Password
      </label>
      <input
        id="confirm-password"
        type="password"
        placeholder="Confirm your password"
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
        required
      />
    </div>
    <div className="flex items-center gap-2">
      <input 
        id="terms" 
        type="checkbox" 
        className="rounded border-input" 
        required 
      />
      <label htmlFor="terms" className="text-sm">
        I agree to the <a href="#" className="text-primary hover:underline">Terms of Service</a> and <a href="#" className="text-primary hover:underline">Privacy Policy</a>
      </label>
    </div>
    <button 
      type="submit" 
      className="w-full bg-primary text-primary-foreground py-2 rounded-md hover:bg-primary/90 transition-colors"
    >
      Create Account
    </button>
  </form>
  <div className="mt-6 text-center text-sm">
    Already have an account? <a href="#" className="text-primary hover:underline">Sign in</a>
  </div>
</div>`,
    preview: `
    <div class="max-w-full mx-auto p-3 bg-card rounded-lg border border-border">
      <h2 class="text-sm font-bold text-center mb-2">Create Account</h2>
      <form class="space-y-2">
        <div class="grid grid-cols-2 gap-2">
          <div>
            <label class="block text-xs font-medium">First Name</label>
            <input type="text" class="w-full rounded border border-input bg-background px-2 py-1 text-xs" />
          </div>
          <div>
            <label class="block text-xs font-medium">Last Name</label>
            <input type="text" class="w-full rounded border border-input bg-background px-2 py-1 text-xs" />
          </div>
        </div>
        <div>
          <label class="block text-xs font-medium">Email</label>
          <input type="email" class="w-full rounded border border-input bg-background px-2 py-1 text-xs" />
        </div>
        <div>
          <label class="block text-xs font-medium">Password</label>
          <input type="password" class="w-full rounded border border-input bg-background px-2 py-1 text-xs" />
        </div>
        <button class="w-full bg-primary text-primary-foreground py-1 rounded text-xs">Create Account</button>
      </form>
    </div>
  `
  },
  {
    id: "data-table",
    name: "Data Table",
    category: "display",
    complexity: "complex",
    tags: ["table", "data", "sortable", "pagination"],
    version: "2.0.0",
    createdAt: "2023-11-05",
    updatedAt: "2023-12-20",
    code: `<div className="w-full">
  <div className="rounded-md border">
    <table className="w-full caption-bottom text-sm">
      <thead className="[&_tr]:border-b">
        <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
          <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">ID</th>
          <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Name</th>
          <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Status</th>
          <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Role</th>
          <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Actions</th>
        </tr>
      </thead>
      <tbody className="[&_tr:last-child]:border-0">
        {[1, 2, 3, 4, 5].map((row) => (
          <tr 
            key={row} 
            className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted"
          >
            <td className="p-4 align-middle font-medium">ITEM-{row}</td>
            <td className="p-4 align-middle">User Name {row}</td>
            <td className="p-4 align-middle">
              <div className="flex w-[100px] items-center">
                <div className="mr-2 h-2 w-2 rounded-full bg-green-500"></div>
                <span>Active</span>
              </div>
            </td>
            <td className="p-4 align-middle">Developer</td>
            <td className="p-4 align-middle">
              <div className="flex items-center space-x-2">
                <button className="text-blue-500 hover:text-blue-700 text-sm">Edit</button>
                <button className="text-red-500 hover:text-red-700 text-sm">Delete</button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
  <div className="flex items-center justify-between space-x-2 py-4">
    <span className="text-sm text-muted-foreground">
      Showing <strong>1</strong> to <strong>5</strong> of <strong>100</strong> results
    </span>
    <div className="flex items-center space-x-2">
      <button className="h-8 w-8 rounded-md border border-input bg-background text-sm font-medium">1</button>
      <button className="h-8 w-8 rounded-md text-sm font-medium text-muted-foreground">2</button>
      <button className="h-8 w-8 rounded-md text-sm font-medium text-muted-foreground">3</button>
      <button className="h-8 w-8 rounded-md text-sm font-medium text-muted-foreground">...</button>
      <button className="h-8 w-8 rounded-md text-sm font-medium text-muted-foreground">10</button>
    </div>
  </div>
</div>`,
    preview: `
      <div class="w-full border rounded-md overflow-hidden">
        <table class="w-full text-xs">
          <thead>
            <tr class="border-b bg-background">
              <th class="p-2 text-left font-medium text-muted-foreground">ID</th>
              <th class="p-2 text-left font-medium text-muted-foreground">Name</th>
              <th class="p-2 text-left font-medium text-muted-foreground">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr class="border-b">
              <td class="p-2">ITEM-1</td>
              <td class="p-2">User Name 1</td>
              <td class="p-2 flex items-center"><div class="mr-1 h-1.5 w-1.5 rounded-full bg-green-500"></div>Active</td>
            </tr>
            <tr class="border-b">
              <td class="p-2">ITEM-2</td>
              <td class="p-2">User Name 2</td>
              <td class="p-2 flex items-center"><div class="mr-1 h-1.5 w-1.5 rounded-full bg-green-500"></div>Active</td>
            </tr>
          </tbody>
        </table>
        <div class="flex justify-between p-2 text-xs">
          <span>5 of 100</span>
          <div class="flex gap-1">
            <button class="h-5 w-5 border rounded">1</button>
            <button class="h-5 w-5 rounded">2</button>
          </div>
        </div>
      </div>
    `
  },
  {
    id: "search-form",
    name: "Search Form with Filters",
    category: "forms",
    complexity: "medium",
    tags: ["form", "search", "filters", "dropdown"],
    version: "1.3.0",
    createdAt: "2023-09-10",
    updatedAt: "2023-12-15",
    code: `<div className="bg-card border border-border rounded-lg p-6 shadow-sm">
  <form>
    <div className="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-4">
      <div className="flex-1">
        <div className="relative">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <input
            type="search"
            placeholder="Search for anything..."
            className="w-full rounded-md border border-input bg-background py-2 pl-10 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
      </div>
      <div className="flex flex-col space-y-4 md:flex-row md:space-y-0 md:space-x-4">
        <select className="rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <option value="">All Categories</option>
          <option value="electronics">Electronics</option>
          <option value="clothing">Clothing</option>
          <option value="books">Books</option>
        </select>
        <select className="rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <option value="">Price Range</option>
          <option value="0-50">$0 - $50</option>
          <option value="50-100">$50 - $100</option>
          <option value="100+">$100+</option>
        </select>
        <button type="submit" className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
          Search
        </button>
      </div>
    </div>
    <div className="mt-4 flex flex-wrap items-center gap-2">
      <span className="text-sm text-muted-foreground">Popular:</span>
      <button type="button" className="rounded-full bg-secondary px-3 py-1 text-xs hover:bg-secondary/80">Electronics</button>
      <button type="button" className="rounded-full bg-secondary px-3 py-1 text-xs hover:bg-secondary/80">New Arrivals</button>
      <button type="button" className="rounded-full bg-secondary px-3 py-1 text-xs hover:bg-secondary/80">Discounted</button>
    </div>
  </form>
</div>`,
    preview: `
      <div class="bg-card border border-border rounded-lg p-3 shadow-sm">
        <form>
          <div class="flex flex-col space-y-2">
            <div class="relative">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="absolute left-2 top-1.5 h-3 w-3 text-muted-foreground">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input type="search" placeholder="Search..." class="w-full rounded-md border border-input bg-background py-1 pl-8 text-xs" />
            </div>
            <div class="flex space-x-2">
              <select class="rounded-md border border-input bg-background px-2 py-1 text-xs">
                <option value="">Categories</option>
              </select>
              <button type="submit" class="rounded-md bg-primary px-2 py-1 text-xs text-primary-foreground">
                Search
              </button>
            </div>
          </div>
        </form>
      </div>
    `
  },
  {
    id: "breadcrumb-navigation",
    name: "Breadcrumb Navigation",
    category: "navigation",
    complexity: "simple",
    tags: ["breadcrumb", "navigation", "path", "link"],
    version: "1.0.0",
    createdAt: "2023-10-15",
    updatedAt: "2023-11-20",
    code: `<nav className="flex" aria-label="Breadcrumb">
  <ol className="flex items-center space-x-2">
    <li>
      <a href="#" className="text-sm font-medium hover:underline">
        Home
      </a>
    </li>
    <li className="flex items-center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-4 w-4 text-muted-foreground"
      >
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
      <a href="#" className="ml-2 text-sm font-medium hover:underline">
        Products
      </a>
    </li>
    <li className="flex items-center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-4 w-4 text-muted-foreground"
      >
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
      <a href="#" className="ml-2 text-sm font-medium hover:underline">
        Categories
      </a>
    </li>
    <li className="flex items-center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-4 w-4 text-muted-foreground"
      >
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
      <span className="ml-2 text-sm font-medium text-muted-foreground">
        Current Page
      </span>
    </li>
  </ol>
</nav>`,
    preview: `
      <nav class="flex" aria-label="Breadcrumb">
        <ol class="flex items-center space-x-1">
          <li>
            <a href="#" class="text-xs hover:underline">Home</a>
          </li>
          <li class="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3 text-muted-foreground">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
            <a href="#" class="ml-1 text-xs hover:underline">Products</a>
          </li>
          <li class="flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3 text-muted-foreground">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
            <span class="ml-1 text-xs text-muted-foreground">Current</span>
          </li>
        </ol>
      </nav>
    `
  },
  {
    id: "sticky-header-layout",
    name: "Sticky Header Layout",
    category: "layout",
    complexity: "medium",
    tags: ["layout", "header", "sticky", "responsive"],
    version: "1.1.0",
    createdAt: "2023-11-01",
    updatedAt: "2023-12-10",
    code: `<div className="min-h-screen flex flex-col">
  <header className="sticky top-0 z-40 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
    <div className="container flex h-14 items-center">
      <div className="mr-4 flex">
        <a href="#" className="flex items-center space-x-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-6 w-6"
          >
            <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z"></path>
            <path d="M3 6h18"></path>
            <path d="M16 10a4 4 0 0 1-8 0"></path>
          </svg>
          <span className="font-bold">Company Name</span>
        </a>
      </div>
      <nav className="hidden md:flex flex-1 items-center justify-between">
        <ul className="flex items-center gap-6 text-sm">
          <li>
            <a href="#" className="font-medium transition-colors hover:text-primary">
              Home
            </a>
          </li>
          <li>
            <a href="#" className="font-medium transition-colors hover:text-primary">
              Products
            </a>
          </li>
          <li>
            <a href="#" className="font-medium transition-colors hover:text-primary">
              About
            </a>
          </li>
          <li>
            <a href="#" className="font-medium transition-colors hover:text-primary">
              Contact
            </a>
          </li>
        </ul>
        <div className="flex items-center gap-2">
          <button className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
            Sign Up
          </button>
        </div>
      </nav>
      <button className="flex items-center space-x-2 md:hidden ml-auto">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-6 w-6"
        >
          <line x1="4" x2="20" y1="12" y2="12"></line>
          <line x1="4" x2="20" y1="6" y2="6"></line>
          <line x1="4" x2="20" y1="18" y2="18"></line>
        </svg>
      </button>
    </div>
  </header>
  <main className="flex-1 container py-10">
    <h1 className="text-3xl font-bold mb-6">Page Title</h1>
    <div className="grid gap-6">
      <p className="text-muted-foreground">
        This is a layout with a sticky header that stays at the top as you scroll. The main content area takes up the remaining space.
      </p>
      <div className="p-6 border border-border rounded-lg bg-card">
        <h2 className="text-xl font-semibold mb-4">Content Section</h2>
        <p className="text-muted-foreground">
          Your page content goes here. As you scroll, the header will remain visible at the top of the viewport.
        </p>
      </div>
    </div>
  </main>
  <footer className="border-t border-border py-6">
    <div className="container flex flex-col md:flex-row justify-between items-center">
      <p className="text-sm text-muted-foreground">
        © 2023 Company Name. All rights reserved.
      </p>
      <div className="flex items-center space-x-4 mt-4 md:mt-0">
        <a href="#" className="text-muted-foreground hover:text-foreground text-sm">
          Privacy Policy
        </a>
        <a href="#" className="text-muted-foreground hover:text-foreground text-sm">
          Terms of Service
        </a>
      </div>
    </div>
  </footer>
</div>`,
    preview: `
      <div class="min-h-32 flex flex-col border border-border rounded-md overflow-hidden">
        <header class="sticky top-0 z-40 w-full border-b border-border bg-background">
          <div class="flex h-8 items-center px-3">
            <div class="flex">
              <a href="#" class="flex items-center">
                <span class="font-bold text-xs">Company</span>
              </a>
            </div>
            <nav class="flex flex-1 justify-between">
              <ul class="hidden sm:flex items-center gap-2 text-xs">
                <li><a href="#" class="text-xs">Home</a></li>
                <li><a href="#" class="text-xs">About</a></li>
              </ul>
              <button class="ml-auto text-xs bg-primary px-2 py-0.5 rounded-md text-primary-foreground">
                Sign Up
              </button>
            </nav>
          </div>
        </header>
        <main class="flex-1 p-2">
          <h1 class="text-sm font-bold">Page Title</h1>
          <p class="text-xs text-muted-foreground mt-1">Content here...</p>
        </main>
      </div>
    `
  },
  {
    id: "notification-toast",
    name: "Notification Toast",
    category: "display",
    complexity: "simple",
    tags: ["notification", "toast", "alert", "message"],
    version: "1.2.0",
    createdAt: "2023-10-20",
    updatedAt: "2023-11-15",
    code: `<div className="fixed bottom-4 right-4 flex flex-col gap-2">
  <div className="bg-background border border-border shadow-lg rounded-lg p-4 flex items-start space-x-4 max-w-md animate-in slide-in-from-right">
    <div className="rounded-full bg-green-500/20 p-2">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-green-500">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
      </svg>
    </div>
    <div className="flex-1">
      <div className="flex justify-between items-start">
        <h5 className="font-medium text-sm">Success Message</h5>
        <button className="text-muted-foreground hover:text-foreground">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      <p className="text-xs text-muted-foreground mt-1">Your action was completed successfully!</p>
    </div>
  </div>
  
  <div className="bg-background border border-border shadow-lg rounded-lg p-4 flex items-start space-x-4 max-w-md animate-in slide-in-from-right">
    <div className="rounded-full bg-amber-500/20 p-2">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-amber-500">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
        <line x1="12" y1="9" x2="12" y2="13"></line>
        <line x1="12" y1="17" x2="12.01" y2="17"></line>
      </svg>
    </div>
    <div className="flex-1">
      <div className="flex justify-between items-start">
        <h5 className="font-medium text-sm">Warning Alert</h5>
        <button className="text-muted-foreground hover:text-foreground">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
      <p className="text-xs text-muted-foreground mt-1">Please review your information before proceeding.</p>
    </div>
  </div>
</div>`,
    preview: `
      <div class="flex flex-col gap-2">
        <div class="bg-background border border-border shadow-sm rounded-lg p-2 flex items-start space-x-2 max-w-[200px]">
          <div class="rounded-full bg-green-500/20 p-1">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3 text-green-500">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
          </div>
          <div class="flex-1">
            <h5 class="font-medium text-xs">Success</h5>
            <p class="text-[10px] text-muted-foreground">Action completed!</p>
          </div>
        </div>
      </div>
    `
  },
  {
    id: "pricing-card",
    name: "Pricing Card",
    category: "cards",
    complexity: "medium",
    tags: ["card", "pricing", "subscription", "features"],
    version: "1.0.0",
    createdAt: "2023-11-25",
    updatedAt: "2023-12-05",
    code: `<div className="rounded-xl border border-border bg-card shadow-sm">
  <div className="p-6">
    <h3 className="text-2xl font-bold">Pro Plan</h3>
    <div className="mt-4 flex items-baseline">
      <span className="text-3xl font-bold">$29</span>
      <span className="ml-1 text-sm text-muted-foreground">/month</span>
    </div>
    <p className="mt-2 text-sm text-muted-foreground">
      Perfect for small businesses and growing teams.
    </p>
    <button className="mt-6 w-full rounded-md bg-primary py-2.5 text-center text-sm font-medium text-primary-foreground hover:bg-primary/90">
      Get Started
    </button>
  </div>
  <div className="p-6 pt-0">
    <div className="mt-6">
      <h4 className="text-sm font-medium">What's included:</h4>
      <ul className="mt-4 space-y-3">
        <li className="flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="mr-2 h-4 w-4 text-green-500"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span className="text-sm">Up to 10 users</span>
        </li>
        <li className="flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="mr-2 h-4 w-4 text-green-500"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span className="text-sm">20GB storage</span>
        </li>
        <li className="flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="mr-2 h-4 w-4 text-green-500"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span className="text-sm">Priority support</span>
        </li>
        <li className="flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="mr-2 h-4 w-4 text-green-500"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span className="text-sm">Advanced analytics</span>
        </li>
      </ul>
    </div>
  </div>
</div>`,
    preview: `
      <div class="rounded-lg border border-border bg-card shadow-sm w-full max-w-[180px]">
        <div class="p-3">
          <h3 class="text-sm font-bold">Pro Plan</h3>
          <div class="mt-1 flex items-baseline">
            <span class="text-lg font-bold">$29</span>
            <span class="ml-1 text-[10px] text-muted-foreground">/month</span>
          </div>
          <button class="mt-2 w-full rounded-md bg-primary py-1 text-[10px] text-primary-foreground">
            Get Started
          </button>
        </div>
        <div class="p-3 pt-0">
          <h4 class="text-[10px] font-medium">What's included:</h4>
          <ul class="mt-1 space-y-1">
            <li class="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-1 h-2 w-2 text-green-500">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              <span class="text-[10px]">10 users</span>
            </li>
            <li class="flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-1 h-2 w-2 text-green-500">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              <span class="text-[10px]">Priority support</span>
            </li>
          </ul>
        </div>
      </div>
    `
  },
  {
    id: "animated-button",
    name: "Animated Button",
    description: "A button with smooth hover and click animations",
    category: "interactive",
    complexity: "simple",
    tags: ["button", "animation"],
    preview: `
      <button class="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:scale-105 transition-transform">
        Click Me
      </button>
    `,
    code: `
      <button className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:scale-105 transition-transform">
        Click Me
      </button>
    `
  },
  {
    id: "feature-card",
    name: "Feature Card",
    description: "A modern feature card with icon, title, and description",
    category: "cards",
    complexity: "medium",
    tags: ["card", "feature", "icon"],
    preview: `
      <div class="border border-border rounded-lg p-6 bg-card hover:border-primary/40 transition-colors">
        <div class="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <svg class="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <h3 class="text-lg font-medium mb-2">Lightning Fast</h3>
        <p class="text-muted-foreground">Our platform delivers exceptional performance with minimal latency.</p>
      </div>
    `,
    code: `
      <div className="border border-border rounded-lg p-6 bg-card hover:border-primary/40 transition-colors">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium mb-2">Lightning Fast</h3>
        <p className="text-muted-foreground">Our platform delivers exceptional performance with minimal latency.</p>
      </div>
    `
  },
  {
    id: "pricing-card",
    name: "Pricing Card",
    description: "A modern pricing card with features list",
    category: "cards",
    complexity: "simple",
    tags: ["card", "pricing", "list"],
    preview: `
      <div class="border border-border rounded-lg p-6 bg-card">
        <h3 class="text-lg font-medium mb-2">Pro Plan</h3>
        <p class="text-2xl font-bold mb-4">$29<span class="text-sm text-muted-foreground">/month</span></p>
        <ul class="space-y-2 mb-6">
          <li class="flex items-center gap-2 text-sm">
            <svg class="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            All Pro Features
          </li>
          <li class="flex items-center gap-2 text-sm">
            <svg class="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            24/7 Support
          </li>
        </ul>
        <button class="w-full bg-primary text-primary-foreground py-2 rounded-md">Get Started</button>
      </div>
    `,
    code: `
      <div className="border border-border rounded-lg p-6 bg-card">
        <h3 className="text-lg font-medium mb-2">Pro Plan</h3>
        <p className="text-2xl font-bold mb-4">$29<span className="text-sm text-muted-foreground">/month</span></p>
        <ul className="space-y-2 mb-6">
          <li className="flex items-center gap-2 text-sm">
            <svg className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            All Pro Features
          </li>
          <li className="flex items-center gap-2 text-sm">
            <svg className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            24/7 Support
          </li>
        </ul>
        <button className="w-full bg-primary text-primary-foreground py-2 rounded-md">Get Started</button>
      </div>
    `
  },
  {
    id: "testimonial-card",
    name: "Testimonial Card",
    description: "A card displaying a customer testimonial",
    category: "cards",
    complexity: "simple",
    tags: ["card", "testimonial", "quote"],
    preview: `
      <div class="border border-border rounded-lg p-6 bg-card">
        <div class="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 rounded-full bg-muted"></div>
          <div>
            <h4 className="font-medium">John Doe</h4>
            <p className="text-sm text-muted-foreground">CEO, Company Inc.</p>
          </div>
        </div>
        <p className="text-muted-foreground italic">"This product has transformed how we do business. Highly recommended!"</p>
      </div>
    `,
    code: `
      <div className="border border-border rounded-lg p-6 bg-card">
        <div class="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 rounded-full bg-muted"></div>
          <div>
            <h4 className="font-medium">John Doe</h4>
            <p className="text-sm text-muted-foreground">CEO, Company Inc.</p>
          </div>
        </div>
        <p className="text-muted-foreground italic">"This product has transformed how we do business. Highly recommended!"</p>
      </div>
    `
  },
  {
    id: "stats-card",
    name: "Stats Card",
    description: "A card displaying statistics with icons",
    category: "cards",
    complexity: "simple",
    tags: ["card", "stats", "metrics"],
    preview: `
      <div class="border border-border rounded-lg p-6 bg-card">
        <div class="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <svg class="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Total Revenue</p>
            <h3 className="text-2xl font-bold">$45,231</h3>
            <p className="text-sm text-green-500">+20.1% from last month</p>
          </div>
        </div>
      </div>
    `,
    code: `
      <div className="border border-border rounded-lg p-6 bg-card">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Total Revenue</p>
            <h3 className="text-2xl font-bold">$45,231</h3>
            <p className="text-sm text-green-500">+20.1% from last month</p>
          </div>
        </div>
      </div>
    `
  },
  {
    id: "file-uploader",
    name: "File Uploader",
    category: "forms",
    complexity: "complex",
    tags: ["form", "file", "upload"],
    version: "1.0.0",
    createdAt: "2023-11-20",
    updatedAt: "2023-12-10",
    code: `<div className="max-w-md mx-auto p-6 bg-card rounded-lg border border-border shadow-sm">
  <h2 className="text-2xl font-bold mb-6 text-center">Upload Files</h2>
  <form className="space-y-4">
    <div className="space-y-2">
      <label htmlFor="file" className="block text-sm font-medium">
        Select Files
      </label>
      <input
        id="file"
        type="file"
        multiple
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
      />
    </div>
    <div className="space-y-2">
      <label htmlFor="description" className="block text-sm font-medium">
        Description
      </label>
      <textarea
        id="description"
        placeholder="Add a description for your files"
        rows={4}
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[100px]"
      />
    </div>
    <div className="pt-2">
      <button 
        type="submit" 
        className="w-full bg-primary text-primary-foreground py-2 rounded-md hover:bg-primary/90 transition-colors"
      >
        Upload Files
      </button>
    </div>
  </form>
</div>`,
    preview: `
      <div class="max-w-full mx-auto p-4 bg-card rounded-lg border border-border shadow-sm">
        <h2 class="text-lg font-bold mb-4 text-center">Upload Files</h2>
        <form class="space-y-3">
          <div class="space-y-1">
            <label class="block text-xs font-medium">Select Files</label>
            <input type="file" multiple class="w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs" />
          </div>
          <div class="space-y-1">
            <label class="block text-xs font-medium">Description</label>
            <textarea placeholder="Add a description for your files" class="w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs min-h-[60px]"></textarea>
          </div>
          <button class="w-full bg-primary text-primary-foreground py-1.5 rounded-md hover:bg-primary/90 transition-colors text-xs font-medium">Upload Files</button>
        </form>
      </div>
    `
  },
  {
    id: "kanban-board",
    name: "Kanban Board",
    description: "Modern task management board with drag-and-drop capabilities",
    category: "application",
    complexity: "complex",
    tags: ["project", "management", "kanban", "task"],
    version: "1.0.0",
    createdAt: "2023-11-25",
    updatedAt: "2023-12-15",
    code: `<div className="w-full max-w-5xl mx-auto p-4">
  <div className="flex gap-4 overflow-x-auto pb-4">
    <div className="flex-shrink-0 w-72">
      <div className="bg-muted p-3 rounded-md">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-medium text-sm">To Do <span className="text-xs text-muted-foreground ml-1">3</span></h3>
          <button className="text-muted-foreground hover:text-foreground">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>
          </button>
        </div>
        <div className="space-y-2">
          <div className="bg-card p-3 rounded-md border shadow-sm">
            <div className="text-xs font-medium mb-1">Implement authentication</div>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-1">
                <span className="bg-red-500/20 text-red-500 text-[10px] px-1.5 py-0.5 rounded-sm font-medium">High</span>
              </div>
              <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-[10px] text-primary">JD</div>
            </div>
          </div>
          <div className="bg-card p-3 rounded-md border shadow-sm">
            <div className="text-xs font-medium mb-1">Set up CI/CD pipeline</div>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-1">
                <span className="bg-amber-500/20 text-amber-500 text-[10px] px-1.5 py-0.5 rounded-sm font-medium">Medium</span>
              </div>
              <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-[10px] text-primary">TS</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div className="flex-shrink-0 w-72">
      <div className="bg-muted p-3 rounded-md">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-medium text-sm">In Progress <span className="text-xs text-muted-foreground ml-1">2</span></h3>
          <button className="text-muted-foreground hover:text-foreground">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>
          </button>
        </div>
        <div className="space-y-2">
          <div className="bg-card p-3 rounded-md border shadow-sm">
            <div className="text-xs font-medium mb-1">Design system components</div>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-1">
                <span className="bg-amber-500/20 text-amber-500 text-[10px] px-1.5 py-0.5 rounded-sm font-medium">Medium</span>
              </div>
              <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-[10px] text-primary">AK</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div className="flex-shrink-0 w-72">
      <div className="bg-muted p-3 rounded-md">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-medium text-sm">Done <span className="text-xs text-muted-foreground ml-1">4</span></h3>
          <button className="text-muted-foreground hover:text-foreground">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>
          </button>
        </div>
        <div className="space-y-2">
          <div className="bg-card p-3 rounded-md border shadow-sm">
            <div className="text-xs font-medium mb-1">Project documentation</div>
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-1">
                <span className="bg-green-500/20 text-green-500 text-[10px] px-1.5 py-0.5 rounded-sm font-medium">Low</span>
              </div>
              <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-[10px] text-primary">JD</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>`,
    preview: `
      <div class="w-full max-w-5xl mx-auto p-2">
        <div class="flex gap-2 overflow-x-auto pb-2">
          <div class="flex-shrink-0 w-36">
            <div class="bg-muted p-2 rounded-md">
              <div class="flex justify-between items-center mb-2">
                <h3 class="font-medium text-xs">To Do <span class="text-[9px] text-muted-foreground ml-1">3</span></h3>
              </div>
              <div class="space-y-1">
                <div class="bg-card p-2 rounded-md border shadow-sm">
                  <div class="text-[10px] font-medium mb-1">Implement auth</div>
                  <div class="flex justify-between items-center">
                    <span class="bg-red-500/20 text-red-500 text-[8px] px-1 py-0.5 rounded-sm font-medium">High</span>
                    <div class="w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center text-[8px] text-primary">JD</div>
                  </div>
                </div>
                <div class="bg-card p-2 rounded-md border shadow-sm">
                  <div class="text-[10px] font-medium mb-1">Set up CI/CD</div>
                  <span class="bg-amber-500/20 text-amber-500 text-[8px] px-1 py-0.5 rounded-sm font-medium">Medium</span>
                </div>
              </div>
            </div>
          </div>
          <div class="flex-shrink-0 w-36">
            <div class="bg-muted p-2 rounded-md">
              <div class="flex justify-between items-center mb-2">
                <h3 class="font-medium text-xs">In Progress <span class="text-[9px] text-muted-foreground ml-1">2</span></h3>
              </div>
              <div class="space-y-1">
                <div class="bg-card p-2 rounded-md border shadow-sm">
                  <div class="text-[10px] font-medium mb-1">Design components</div>
                  <span class="bg-amber-500/20 text-amber-500 text-[8px] px-1 py-0.5 rounded-sm font-medium">Medium</span>
                </div>
              </div>
            </div>
          </div>
          <div class="flex-shrink-0 w-36">
            <div class="bg-muted p-2 rounded-md">
              <div class="flex justify-between items-center mb-2">
                <h3 class="font-medium text-xs">Done <span class="text-[9px] text-muted-foreground ml-1">4</span></h3>
              </div>
              <div class="space-y-1">
                <div class="bg-card p-2 rounded-md border shadow-sm">
                  <div class="text-[10px] font-medium mb-1">Documentation</div>
                  <span class="bg-green-500/20 text-green-500 text-[8px] px-1 py-0.5 rounded-sm font-medium">Low</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `
  },
  {
    id: "data-grid",
    name: "Data Grid",
    description: "Interactive data table with sorting, filtering, and pagination capabilities",
    category: "application",
    complexity: "medium",
    tags: ["table", "data", "grid", "sortable"],
    version: "1.0.0",
    createdAt: "2023-11-25",
    updatedAt: "2023-12-15",
    code: `<div className="w-full border rounded-lg">
  <div className="flex items-center justify-between p-4 border-b">
    <div className="flex items-center space-x-2">
      <h2 className="text-lg font-semibold">Products</h2>
      <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-md">
        145 items
      </span>
    </div>
    <div className="flex items-center gap-2">
      <div className="relative">
        <svg
          className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </svg>
        <input
          type="search"
          placeholder="Search products..."
          className="pl-8 h-9 w-64 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
      </div>
      <button className="inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground px-3 py-1 text-xs h-9">
        <svg
          className="mr-1 h-3.5 w-3.5"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 16v4a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-4" />
          <path d="M12 2v16" />
          <path d="m16 6-4-4-4 4" />
        </svg>
        Export
      </button>
    </div>
  </div>
  <div className="relative w-full overflow-auto">
    <table className="w-full caption-bottom text-sm">
      <thead className="[&_tr]:border-b bg-muted/50">
        <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
          <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground">
            <div className="flex items-center space-x-2">
              <button className="hidden h-4 w-4 rounded border-muted bg-background sm:inline-flex">
                <svg
                  className="h-4 w-4 text-primary-foreground"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </button>
            </div>
          </th>
          <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground">
            <div className="flex items-center space-x-2">
              <span>Product</span>
              <svg
                className="h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m7 15 5 5 5-5" />
                <path d="m7 9 5-5 5 5" />
              </svg>
            </div>
          </th>
          <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground">Status</th>
          <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground">Category</th>
          <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground">
            <div className="flex items-center space-x-2">
              <span>Price</span>
              <svg
                className="h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m7 15 5 5 5-5" />
                <path d="m7 9 5-5 5 5" />
              </svg>
            </div>
          </th>
          <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground">Actions</th>
        </tr>
      </thead>
      <tbody className="[&_tr:last-child]:border-0">
        <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
          <td className="p-4 align-middle">
            <button className="h-4 w-4 rounded border border-muted bg-background"></button>
          </td>
          <td className="p-4 align-middle font-medium">iPhone 14 Pro</td>
          <td className="p-4 align-middle">
            <div className="flex w-[100px] items-center">
              <div className="flex items-center gap-2">
                <div className="h-2.5 w-2.5 rounded-full bg-green-500"></div>
                <span>In stock</span>
              </div>
            </div>
          </td>
          <td className="p-4 align-middle">Smartphones</td>
          <td className="p-4 align-middle">$999.00</td>
          <td className="p-4 align-middle text-right">
            <button className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground shadow hover:bg-primary/90 h-8 px-3 py-2">
              Edit
            </button>
          </td>
        </tr>
        <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
          <td className="p-4 align-middle">
            <button className="h-4 w-4 rounded border border-muted bg-background"></button>
          </td>
          <td className="p-4 align-middle font-medium">MacBook Pro 16"</td>
          <td className="p-4 align-middle">
            <div className="flex w-[100px] items-center">
              <div className="flex items-center gap-2">
                <div className="h-2.5 w-2.5 rounded-full bg-green-500"></div>
                <span>In stock</span>
              </div>
            </div>
          </td>
          <td className="p-4 align-middle">Laptops</td>
          <td className="p-4 align-middle">$2,499.00</td>
          <td className="p-4 align-middle text-right">
            <button className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground shadow hover:bg-primary/90 h-8 px-3 py-2">
              Edit
            </button>
          </td>
        </tr>
        <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
          <td className="p-4 align-middle">
            <button className="h-4 w-4 rounded border border-muted bg-background"></button>
          </td>
          <td className="p-4 align-middle font-medium">AirPods Pro</td>
          <td className="p-4 align-middle">
            <div className="flex w-[100px] items-center">
              <div className="flex items-center gap-2">
                <div className="h-2.5 w-2.5 rounded-full bg-yellow-500"></div>
                <span>Low stock</span>
              </div>
            </div>
          </td>
          <td className="p-4 align-middle">Audio</td>
          <td className="p-4 align-middle">$249.00</td>
          <td className="p-4 align-middle text-right">
            <button className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground shadow hover:bg-primary/90 h-8 px-3 py-2">
              Edit
            </button>
          </td>
        </tr>
        <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
          <td className="p-4 align-middle">
            <button className="h-4 w-4 rounded border border-muted bg-background"></button>
          </td>
          <td className="p-4 align-middle font-medium">iPad Air</td>
          <td className="p-4 align-middle">
            <div className="flex w-[100px] items-center">
              <div className="flex items-center gap-2">
                <div className="h-2.5 w-2.5 rounded-full bg-red-500"></div>
                <span>Out of stock</span>
              </div>
            </div>
          </td>
          <td className="p-4 align-middle">Tablets</td>
          <td className="p-4 align-middle">$599.00</td>
          <td className="p-4 align-middle text-right">
            <button className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground shadow hover:bg-primary/90 h-8 px-3 py-2">
              Edit
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
  <div className="flex items-center justify-end px-4 py-4">
    <div className="flex items-center space-x-6 lg:space-x-8">
      <div className="flex items-center space-x-2">
        <p className="text-sm font-medium">Rows per page</p>
        <button className="inline-flex items-center justify-center px-3 h-8 rounded-md text-sm font-medium transition-colors border border-input bg-background hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
          10
          <svg
            className="ml-1 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      </div>
      <div className="flex w-[100px] items-center justify-center text-sm font-medium">
        Page 1 of 10
      </div>
      <div className="flex items-center space-x-2">
        <button className="inline-flex items-center justify-center px-3 h-8 rounded-md text-sm font-medium transition-colors border border-input bg-background hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
          <svg
            className="h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>
        <button className="inline-flex items-center justify-center px-3 h-8 rounded-md text-sm font-medium transition-colors border border-input bg-background hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
          <svg
            className="h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>
      </div>
    </div>
  </div>
</div>`,
    preview: `
      <div class="w-full border rounded-lg overflow-hidden">
        <div class="flex items-center justify-between p-2 border-b">
          <div class="flex items-center space-x-1">
            <h2 class="text-xs font-semibold">Products</h2>
            <span class="text-[8px] text-muted-foreground bg-muted px-1 py-0.5 rounded">
              145
            </span>
          </div>
          <div class="flex items-center gap-1">
            <div class="relative">
              <input type="search" placeholder="Search..." class="text-[8px] h-5 w-20 rounded px-1 py-0.5 border" />
            </div>
          </div>
        </div>
        <div class="relative w-full overflow-auto">
          <table class="w-full caption-bottom text-[9px]">
            <thead class="[&_tr]:border-b bg-muted/50">
              <tr class="border-b transition-colors hover:bg-muted/50">
                <th class="h-5 px-1 text-left align-middle font-medium text-muted-foreground">
                  <div class="flex items-center">
                    <span>#</span>
                  </div>
                </th>
                <th class="h-5 px-1 text-left align-middle font-medium text-muted-foreground">
                  <div class="flex items-center space-x-1">
                    <span>Product</span>
                    <svg class="h-2 w-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="m7 15 5 5 5-5" /><path d="m7 9 5-5 5 5" />
                    </svg>
                  </div>
                </th>
                <th class="h-5 px-1 text-left align-middle font-medium text-muted-foreground">Status</th>
                <th class="h-5 px-1 text-left align-middle font-medium text-muted-foreground">Price</th>
              </tr>
            </thead>
            <tbody class="[&_tr:last-child]:border-0">
              <tr class="border-b transition-colors hover:bg-muted/50">
                <td class="py-1 px-1 align-middle">1</td>
                <td class="py-1 px-1 align-middle font-medium">iPhone 14 Pro</td>
                <td class="py-1 px-1 align-middle">
                  <div class="flex items-center">
                    <div class="h-1.5 w-1.5 rounded-full bg-green-500 mr-1"></div>
                    <span>In stock</span>
                  </div>
                </td>
                <td class="py-1 px-1 align-middle">$999</td>
              </tr>
              <tr class="border-b transition-colors hover:bg-muted/50">
                <td class="py-1 px-1 align-middle">2</td>
                <td class="py-1 px-1 align-middle font-medium">MacBook Pro</td>
                <td class="py-1 px-1 align-middle">
                  <div class="flex items-center">
                    <div class="h-1.5 w-1.5 rounded-full bg-green-500 mr-1"></div>
                    <span>In stock</span>
                  </div>
                </td>
                <td class="py-1 px-1 align-middle">$2,499</td>
              </tr>
              <tr class="border-b transition-colors hover:bg-muted/50">
                <td class="py-1 px-1 align-middle">3</td>
                <td class="py-1 px-1 align-middle font-medium">AirPods Pro</td>
                <td class="py-1 px-1 align-middle">
                  <div class="flex items-center">
                    <div class="h-1.5 w-1.5 rounded-full bg-yellow-500 mr-1"></div>
                    <span>Low stock</span>
                  </div>
                </td>
                <td class="py-1 px-1 align-middle">$249</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="flex items-center justify-end p-1 text-[8px]">
          <div class="flex items-center space-x-1">
            <span>Page 1 of 10</span>
            <button class="inline-flex items-center justify-center h-4 w-4 rounded border">
              <svg class="h-2 w-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <button class="inline-flex items-center justify-center h-4 w-4 rounded border">
              <svg class="h-2 w-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    `
  }
];
